import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Usuario } from '../models/usuario';
import { JwtDecoderService } from './jwt-decoder.service';
import { environment } from 'environments/environment';

@Injectable()
export class AutenticacaoService {
  private apiUrlAuth = `${environment.urlAPI}/auth`;
  private readonly ACEITE_TERMO_DE_USO = 1;

  constructor(
    private http: HttpClient,
    private jwtDecoder: JwtDecoderService
  ) { }

  /**
   * Verifica se o usuário está logado
   */
  get logado(): boolean {
    const token = this.userToken;
    
    if (!token) return false;
    
    return this.jwtDecoder.isValidJwtStructure(token) && 
           !this.jwtDecoder.isTokenExpired(token);
  }

  /**
   * Define o estado de login do usuário (compatibilidade)
   */
  set logado(value: boolean) {
    if (!value) {
      this.logoff();
    }
  }

  /**
   * Obtém o token do usuário logado
   */
  get userToken(): string {
    return localStorage.getItem('userToken') || sessionStorage.getItem('userToken') || '';
  }

  /**
   * Define o token do usuário
   */
  set userToken(value: string) {
    const storage = this.manterLogado ? localStorage : sessionStorage;
    const altStorage = this.manterLogado ? sessionStorage : localStorage;
    
    storage.setItem('userToken', value);
    altStorage.removeItem('userToken');
  }

  /**
   * Obtém os dados do usuário logado decodificando o JWT
   */
  get user(): Usuario {
    const token = this.userToken;
    
    if (!token) return new Usuario();
    
    const userData = this.jwtDecoder.getUserFromToken(token);
    
    if (!userData) return new Usuario();

    // Converte para o formato esperado pela aplicação
    const usuario = new Usuario();
    usuario.uid = userData.uid;
    usuario.nome = userData.nome;
    usuario.cpf_cnpj = userData.cpf_cnpj;
    usuario.email = userData.email;
    usuario.foto = userData.foto;
    usuario.celular = userData.celular;
    usuario.telefone1 = userData.telefone1;
    usuario.telefone2 = userData.telefone2;
    usuario.status = userData.status;
    usuario.listaCodSistemaGestao = userData.listaCodSistemaGestao;
    usuario.listaSiglaSistemaGestao = userData.listaSiglaSistemaGestao;
    usuario.razaoSocial = userData.razaoSocial;
    usuario.termoDeUso = userData.termoDeUso;

    let perfisUser = '';
    
    // Processa perfis e transações como antes
    if (userData.perfis) {
      usuario.perfil = [];
      perfisUser = userData.perfis;

      userData.perfis.split(',').forEach((x, index) => {
        usuario.perfil.push({ nome: x, codigo: index });
      });
    }
    
    if (userData.transacoes) {
      usuario.transacao = userData.transacoes.split(',');
    }

    usuario.perfis = perfisUser;
    
    return usuario;
  }

  /**
   * Define os dados do usuário (mantido para compatibilidade)
   */
  set user(value: Usuario) {
    console.warn('Setting user data is deprecated. Data comes from JWT token.');
  }

  /**
   * Obtém informações temporárias para login de usuários com perfil duplo
   */
  get userPerfilDuplo(): any {
    const userInfo = sessionStorage.getItem('userInfoPerfilDuplo');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * Define informações de perfil duplo
   */
  set userPerfilDuplo(value: any) {
    sessionStorage.setItem('userInfoPerfilDuplo', JSON.stringify(value));
  }

  /**
   * Verifica se deve manter o usuário logado
   */
  get manterLogado(): boolean {
    const mL = localStorage.getItem('manterLogado');
    return mL ? JSON.parse(mL) : false;
  }

  /**
   * Define se deve manter o usuário logado
   */
  set manterLogado(value: boolean) {
    localStorage.setItem('manterLogado', value.toString());
  }

  /**
   * Realiza login do usuário
   */
  login(login: string, senha: string): Promise<any> {
    const payload = {
      cpf_cnpj: login,
      password: senha
    };

    return new Promise((resolve, reject) => {
      this.http.post(this.apiUrlAuth, payload)
        .subscribe({
          next: (data: any) => {
            let tokenAuth = data.tokenAuth;
            const loginDuplo = data.loginPerfilDuplo;

            if (loginDuplo) {
              tokenAuth = `${data.tokenAuthInterno}|${data.tokenAuthAuditor}`;
            }

            if (tokenAuth) {
              this.userToken = tokenAuth;

              if (loginDuplo) {
                this.userPerfilDuplo = data.data.user;
                resolve({
                  primeiroLogin: false,
                  loginPerfilDuplo: true,
                  termoDeUso: this.userPerfilDuplo.userInterno ? this.userPerfilDuplo.userInterno.termoDeUso : false
                });
              } else {
                // Para usuário único, decodifica o JWT para obter termoDeUso
                const userData = this.jwtDecoder.decodeToken(tokenAuth);
                
                resolve({
                  primeiroLogin: false,
                  loginPerfilDuplo: false,
                  termoDeUso: userData.termoDeUso || false
                });
              }
            } else {
              const primeiroLogin = data.primeiroLogin;

              if (primeiroLogin) {
                resolve({
                  primeiroLogin: true,
                  loginPerfilDuplo: false,
                  termoDeUso: false
                });
              } else {
                reject(data.data);
              }
            }
          },
          error: (error) => {
            console.error('Erro autenticação:');
            reject(error);
          }
        });
    });
  }

  /**
   * Define login com perfil de Auditor
   */
  setLoginPerfilAuditor(): Promise<void> {
    return new Promise<void>((resolve) => {
      const u = this.userPerfilDuplo.userAuditor;

      if (!u.termoDeUso) {
        this.aceiteTermoDeUso(u.cpf_cnpj).catch(console.error);
      }

      this.userToken = this.userToken.split('|')[1];
      
      resolve();
    });
  }

  /**
   * Registra o aceite do Termo de Uso pelo usuário
   */
  aceiteTermoDeUso(cpfCnpj: string): Promise<void> {
    if (!cpfCnpj) {
      return Promise.reject(new Error('CPF/CNPJ é obrigatório'));
    }
    
    const payload = {
      termo: this.ACEITE_TERMO_DE_USO,
      cpf_cnpj: cpfCnpj
    };

    return new Promise<void>((resolve, reject) => {
      this.http.post(`${this.apiUrlAuth}-termo-de-uso`, payload)
        .subscribe({
          next: (response: any) => {
            if (!response.type) {
              reject(new Error(response.message || 'Erro ao registrar aceite'));
            } else {
              resolve();
            }
          },
          error: (error) => {
            const errorMsg = error.message || 'Erro ao registrar aceite do termo de uso';
            console.error(errorMsg);
            reject(new Error(errorMsg));
          }
        });
    });
  }

  /**
   * Define login com perfil Interno
   */
  setLoginPerfilInterno(): Promise<void> {
    return new Promise<void>((resolve) => {
      const u = this.userPerfilDuplo.userInterno;

      if (!u.termoDeUso) {
        this.aceiteTermoDeUso(u.cpf_cnpj).catch(console.error);
      }

      this.userToken = this.userToken.split('|')[0];
      
      resolve();
    });
  }

  /**
   * Realiza o logout do usuário
   */
  logoff(): void {
    sessionStorage.removeItem('userToken');
    localStorage.removeItem('userToken');
    sessionStorage.removeItem('userInfoPerfilDuplo');
    sessionStorage.removeItem('userInfo');
    localStorage.removeItem('userInfo');
    sessionStorage.removeItem('logado');
    localStorage.removeItem('logado');
  }

  /**
   * Valida token de senha
   */
  validaTokenSenha(token: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.apiUrlAuth}-tokenSenha`, { token })
        .subscribe({
          next: (data: any) => {
            if (data.type) {
              resolve(data.data.login);
            } else {
              reject(data.data);
            }
          },
          error: (error) => {
            console.error('Erro na validação de token:');
            reject(error);
          }
        });
    });
  }

  /**
   * Redefine a senha do usuário
   */
  resetSenha(novaSenha: string, token: string): Promise<boolean> {
    const payload = { novaSenha, token };

    return new Promise((resolve, reject) => {
      this.http.post(`${this.apiUrlAuth}-reset`, payload)
        .subscribe({
          next: (data: any) => {
            if (data.type) {
              resolve(true);
            } else {
              reject(
                typeof data.data === 'string'
                  ? data.data
                  : 'Erro ao redefinir senha.',
              );
            }
          },
          error: (error) => {
            console.error('Erro ao redefinir senha:');
            const msg =
              error && error.error ? error.error.data : undefined;
            reject(typeof msg === 'string' ? msg : error);
          }
        });
    });
  }

  /**
   * Solicita redefinição de senha
   */
  solicitaResetSenha(cpf_cnpj: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.apiUrlAuth}-solicitarReset`, { cpf_cnpj })
        .subscribe({
          next: (data: any) => {
            if (data.type) {
              resolve(data.token);
            } else {
              reject(
                typeof data.data === 'string'
                  ? data.data
                  : 'Falha ao solicitar redefinição de senha.',
              );
            }
          },
          error: (error) => {
            console.error('Erro ao solicitar redefinição de senha:');
            const msg =
              error && error.error ? error.error.data : undefined;
            reject(typeof msg === 'string' ? msg : error);
          }
        });
    });
  }

  /**
   * Verifica se o token está próximo do vencimento
   */
  isTokenExpiringSoon(minutesBefore: number = 5): boolean {
    const token = this.userToken;
    if (!token) return false;

    const timeLeft = this.jwtDecoder.getTimeUntilExpiration(token);
    return timeLeft > 0 && timeLeft <= (minutesBefore * 60);
  }

  /**
   * Obtém dados completos do usuário do backend (se necessário)
   */
  getUserDetails(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.apiUrlAuth}/profile`)
        .subscribe({
          next: (data) => resolve(data),
          error: (error) => reject(error)
        });
    });
  }
}


// WEBPACK FOOTER //
// /app/src/app/services/autenticacao.service.ts